import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import { Card, Col, Divider, Row, Tag, Typography } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import dayjs from "dayjs";
import { toast } from "utils/toast";

import { useGetContractDetails } from "hooks/useContracts";

import ShareButton from "js/components/Contracts/ShareButton";
import {
  SkeletonBlock,
  SkeletonDivider,
  SkeletonHeading,
  SkeletonText,
} from "js/components/Loading";
import GuarantorDetailsComponent from "js/components/Tenants/TenantReportDetails/GuarantorDetailsComponent";
import LandlordDetailsComponent from "js/components/Tenants/TenantReportDetails/LandlordDetailsComponent";
import LeaseContractDetailsComponent from "js/components/Tenants/TenantReportDetails/LeaseContractDetailsComponent";
import PropertyDetailsComponent from "js/components/Tenants/TenantReportDetails/PropertyDetailsComponent";
import RequirementDetailsMultiexpedienteComponent from "js/components/Tenants/TenantReportDetails/RequirementDetailsMultiexpedienteComponent";
import TenantDetailsComponent from "js/components/Tenants/TenantReportDetails/TenantDetailsComponent";

const { Title, Text } = Typography;

// Safe numeric parsers
const toInt = (value: unknown, fallback = 0): number => {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) ? n : fallback;
};
const toFloat = (value: unknown, fallback = 0): number => {
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : fallback;
};

interface ContractDetailsData {
  contract: any;
  required_documents: any;
  folder_data: any;
}

const ContractDetails = () => {
  const { contractId } = useParams();
  const screen = useBreakpoint();

  const {
    data: contractData,
    isLoading: loading,
    error,
  } = useGetContractDetails(contractId);

  const [tenantData, setTenantData] = React.useState<any>(null);
  const [landlordData, setLandlordData] = React.useState<any>(null);
  const [guarantorData, setGuarantorData] = React.useState<any>(null);
  const [propertyData, setPropertyData] = React.useState<any>(null);
  const [leaseContractDetails, setLeaseContractDetails] =
    React.useState<any>(null);

  useEffect(() => {
    if (error) {
      toast.error("No se pudo cargar el contrato. Por favor intenta de nuevo.");
    }
  }, [error]);

  const getStatusTag = (status: string): React.JSX.Element => {
    let color;
    let text = "";
    switch (status) {
      case "created":
        color = "blue";
        text = "En progreso";
        break;
      case "pending_signature":
        color = "orange";
        text = "Pendiente de Firma";
        break;
      case "signed":
        color = "green";
        text = "Firmado";
        break;
      case "completed":
        color = "green";
        text = "Completado";
        break;
      default:
        color = "gray";
        text = status;
        break;
    }
    return <Tag color={color}>{text}</Tag>;
  };

  // Extract tenant data from contract.tenants
  const getTenantData = () => {
    const tenants = contractData?.contract?.tenants;
    if (!tenants || tenants.length === 0) return null;

    const tenant = tenants[0];
    return {
      id: tenant.tenant_id || tenant.id,
      verified: tenant.verified || false,
      name: tenant.name || "",
      second_name: tenant.second_name || "",
      first_surname: tenant.first_surname || "",
      second_surname: tenant.second_surname || "",
      email: tenant.email || "",
      phone_number: tenant.phone_number || "",
      dial_code: tenant.dial_code || "",
      birth_date: tenant.birth_date || null,
      rfc: tenant.rfc || null,
      curp: tenant.curp || null,
      legal_type: (tenant.legal_type === 1 ? "Natural" : "Legal") as
        | "Natural"
        | "Legal",
    };
  };

  // Extract landlord data from contract.landlords
  const getLandlordData = () => {
    const landlords = contractData?.contract?.landlords;
    if (!landlords || landlords.length === 0) return null;

    const landlord = landlords[0];
    return {
      id: landlord.landlord_id || landlord.id,
      verified: landlord.verified || false,
      name: landlord.name || "",
      second_name: landlord.second_name || "",
      first_surname: landlord.first_surname || "",
      second_surname: landlord.second_surname || "",
      email: landlord.email || "",
      phone_number: landlord.phone_number || "",
      dial_code: landlord.dial_code || "",
      birth_date: landlord.birth_date || null,
      rfc: landlord.rfc || null,
      curp: landlord.curp || null,
      legal_type: landlord.legal_type || 1,
    };
  };

  // Extract guarantor data from contract.guarantors
  const getGuarantorData = () => {
    const guarantors = contractData?.contract?.guarantors;
    if (!guarantors || guarantors.length === 0) return null;

    const guarantor = guarantors[0];
    return {
      id: guarantor.guarantor_id || guarantor.id,
      verified: guarantor.verified || false,
      name: guarantor.name || "",
      second_name: guarantor.second_name || "",
      first_surname: guarantor.first_surname || "",
      second_surname: guarantor.second_surname || "",
      email: guarantor.email || "",
      phone_number: guarantor.phone_number || "",
      dial_code: guarantor.dial_code || "",
      birth_date: guarantor.birth_date || null,
      rfc: guarantor.rfc || null,
      curp: guarantor.curp || null,
      legal_type: guarantor.legal_type || 1,
    };
  };

  // Extract property data from contract.property
  const getPropertyData = () => {
    const property = contractData?.contract?.property;
    if (!property) return null;

    const rentalAndDeposit = contractData?.contract?.rental_and_deposit;

    // Priority: rental_and_deposit (final) > property (preliminary)
    const rental_price = rentalAndDeposit?.monthly_rent
      ? toFloat(rentalAndDeposit.monthly_rent, 0)
      : property.rental_price
        ? toFloat(property.rental_price, 0)
        : 0;

    const maintenance_price = rentalAndDeposit?.maintenance_price
      ? toFloat(rentalAndDeposit.maintenance_price, 0)
      : property.maintenance_price
        ? toFloat(property.maintenance_price, 0)
        : 0;

    return {
      id: property.property_id || property.id,
      verified: property.verified || false,
      address: {
        street: property.address?.street || "",
        exterior_number: property.address?.exterior_number || "",
        interior_number: property.address?.interior_number || "",
        neighborhood: property.address?.neighborhood || "",
        zip_code: property.address?.zip_code || "",
        city: property.address?.city || "",
        state: property.address?.state || "",
        country: "México",
      },
      alias: property.usage || "",
      rental_price,
      maintenance_price,
    };
  };

  // Extract lease contract details
  const getLeaseContractDetails = () => {
    const contract = contractData?.contract;
    const validityAndDates = contract?.validity_and_dates;
    const rentalAndDeposit = contract?.rental_and_deposit;

    if (!validityAndDates && !rentalAndDeposit) return null;

    // Parse payment_day and grace_period as numbers (they come as strings from the API)
    const payment_day = toInt(validityAndDates?.payment_day, 0);
    const grace_period = toInt(validityAndDates?.grace_period, 0);
    const guarantee_deposit = toFloat(rentalAndDeposit?.deposit_amount, 0);

    // Format dates from ISO to DD/MM/YYYY
    const formatDate = (isoDate: string | null) => {
      if (!isoDate) return null;
      return dayjs(isoDate).format("DD/MM/YYYY");
    };

    return {
      verified: validityAndDates?.verified || false,
      start_date:
        formatDate(validityAndDates?.start_date) ||
        contract?.start_date ||
        null,
      end_date:
        formatDate(validityAndDates?.end_date) || contract?.end_date || null,
      closed_at: contract?.closed_at || null,
      signed_at: contract?.signed_at || "",
      status: contract?.status || "created",
      // Nested objects that the component checks FIRST
      validity_and_dates: {
        payment_day: payment_day,
        grace_period: grace_period,
        payment_method: validityAndDates?.payment_method || null,
        bank_institution: validityAndDates?.bank_institution || null,
        bank_CLABE: validityAndDates?.bank_CLABE || null,
      },
      rental_and_deposit: {
        guarantee_deposit,
      },
      // Flat fields as fallback (component checks these SECOND)
      payment_day,
      grace_period,
      payment_method: validityAndDates?.payment_method || null,
      guarantee_deposit,
    };
  };

  const folioNumber = contractData?.contract?.metadata?.folio_number;
  const folderUrl = contractData?.contract?.metadata?.folder_url;

  useEffect(() => {
    if (contractData) {
      setTenantData(getTenantData());
      setLandlordData(getLandlordData());
      setGuarantorData(getGuarantorData());
      setPropertyData(getPropertyData());
      setLeaseContractDetails(getLeaseContractDetails());
    }
  }, [contractData]);

  // Extract phone numbers for WhatsApp sharing
  const tenantPhoneNumber = useMemo(() => {
    if (!tenantData?.phone_number || !tenantData?.dial_code) return undefined;
    return `${tenantData.dial_code}${tenantData.phone_number}`;
  }, [tenantData]);

  const landlordPhoneNumber = useMemo(() => {
    if (!landlordData?.phone_number || !landlordData?.dial_code)
      return undefined;
    return `${landlordData.dial_code}${landlordData.phone_number}`;
  }, [landlordData]);

  if (loading || !contractData) {
    return (
      <Row>
        <Col className={"mb-2"} xs={24} sm={24} md={24} lg={15}>
          <Card className={"mb-2"}>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <SkeletonHeading className="h-7 w-56" />
                <SkeletonBlock className="h-6 w-24 rounded-full" />
              </div>
              <SkeletonDivider />
              <div className="space-y-3">
                <SkeletonText className="h-5 w-40" />
                <SkeletonText className="h-4 w-full" />
                <SkeletonText className="h-4 w-3/4" />
              </div>
            </div>
          </Card>

          {[1, 2, 3].map((index) => (
            <Card key={`skeleton-card-${index}`} className={"mb-2"}>
              <div className="space-y-4">
                <SkeletonHeading className="h-6 w-48" />
                <SkeletonDivider />
                <div className="space-y-2">
                  <SkeletonText className="h-4 w-full" />
                  <SkeletonText className="h-4 w-5/6" />
                  <SkeletonText className="h-4 w-4/5" />
                </div>
              </div>
            </Card>
          ))}
        </Col>

        <Col
          className={"mb-2"}
          xs={24}
          sm={24}
          md={24}
          lg={{ span: 8, offset: 1 }}
        >
          {[1, 2].map((index) => (
            <Card key={`skeleton-sidebar-${index}`} className={"mb-2"}>
              <div className="space-y-4">
                <SkeletonHeading className="h-6 w-32" />
                <SkeletonDivider />
                <div className="space-y-2">
                  <SkeletonText className="h-4 w-full" />
                  <SkeletonText className="h-4 w-3/4" />
                </div>
              </div>
            </Card>
          ))}
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row>
        <Col className={"mb-2"} xs={24} sm={24} md={24} lg={15}>
          <Card className={"mb-2"}>
            {/* Header Section */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Contrato: Folio #
                  {contractData.contract?.metadata.folio_number}
                </Title>
                <Text type={"secondary"}>
                  Fase: {getStatusTag(contractData.contract?.status)}
                </Text>
              </Col>
              {folderUrl && (
                <Col>
                  <ShareButton
                    contractLink={folderUrl}
                    tenantPhoneNumber={tenantPhoneNumber}
                    landlordPhoneNumber={landlordPhoneNumber}
                    active={!!folderUrl}
                  />
                </Col>
              )}
            </Row>

            <Divider />

            {/* Property Details Section */}
            {propertyData && (
              <PropertyDetailsComponent
                property={propertyData}
                onUpdate={(updatedProperty) => setPropertyData(updatedProperty)}
                folio={folioNumber}
              />
            )}

            {/* Landlord Details Section */}
            {landlordData && (
              <>
                <Divider />
                <LandlordDetailsComponent
                  landlord={landlordData}
                  onUpdate={(updatedLandlord) =>
                    setLandlordData(updatedLandlord)
                  }
                  folio={folioNumber}
                />
              </>
            )}

            <Divider />

            {/* Tenant Details Section */}
            {tenantData && (
              <TenantDetailsComponent
                tenant={tenantData}
                onUpdate={(updatedTenant) => setTenantData(updatedTenant)}
                folio={folioNumber}
                isEditable={!tenantData.verified}
              />
            )}

            {/* Guarantor Details Section */}
            {guarantorData && (
              <>
                <Divider />
                <GuarantorDetailsComponent
                  guarantor={guarantorData}
                  onUpdate={(updatedGuarantor) =>
                    setGuarantorData(updatedGuarantor)
                  }
                  folio={folioNumber}
                  isEditable={false}
                />
              </>
            )}
          </Card>

          {/* Requirements Section */}
          {contractData.required_documents && (
            <RequirementDetailsMultiexpedienteComponent
              requirements={contractData.required_documents}
              tenantReportId={contractData.contract?.id || ""}
              reportUrl={folderUrl || ""}
            />
          )}
        </Col>

        {/* Right Sidebar */}
        <Col
          className={"mb-2"}
          style={screen.lg ? { paddingLeft: 24, width: "100%" } : undefined}
          xs={24}
          sm={24}
          md={24}
          lg={9}
        >
          {/* Lease Contract Details */}
          {leaseContractDetails && (
            <LeaseContractDetailsComponent
              lease_contract={leaseContractDetails}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default ContractDetails;
